import React, { useEffect, useRef, useState } from "react";
import {
  TableRow,
  TableCell,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";

const tdLabelSx = {
  width: 200,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };

const buildFacturaLabel = (factura) => {
  const numero = factura?.numero_documento || "Sin número";
  const fecha = factura?.fecha_emision || "";
  const total = factura?.total || "0.00";
  return `${numero} • ${fecha} • $${total}`;
};

const normalizeLoadedProducts = (productos) => {
  if (!Array.isArray(productos)) return [];

  return productos.map((r) => ({
    product_id: r.product_id ?? r.product_id_ref ?? null,
    variant_id: r.variant_id ?? r.variant_id_ref ?? null,
    code: r.code || "",
    sku: r.sku || "",
    variant_type: r.variant_type || "",
    value: r.value || r.variant_value || "",
    codigo_principal: r.codigo_principal || "",
    codigo_auxiliar: r.codigo_auxiliar || "",
    descripcion: r.descripcion || "",
    detalles: r.detalles || r.detalle || r.detalle_adicional || "",
    cantidad: String(r.cantidad ?? "1"),
    pvp: String(r.pvp ?? r.precio_unitario ?? "0"),
    iva_porcentaje: Number(r.iva_porcentaje ?? 0),
    descuentoPct: String(r.descuentoPct ?? r.descuento_pct ?? "0"),
  }));
};

export default function Ncredit({
  persona,
  facturaRelacionadaId,
  facturaRelacionadaNumero,
  facturaRelacionadaClaveAcceso,
  motivoTipo,
  onChange,
  onProductsLoaded,
}) {
  const [facturasCliente, setFacturasCliente] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [facturasError, setFacturasError] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);

  const lastClienteFacturaRef = useRef("");

  const clienteSeleccionado = Boolean(persona?.identificacion);
  const identificacion = String(persona?.identificacion || "").trim();

  useEffect(() => {
    if (!identificacion) {
      setFacturasCliente([]);
      setFacturasError("");
      setLoadingFacturas(false);
      lastClienteFacturaRef.current = "";
      onChange?.({
        factura_relacionada_id: "",
        factura_relacionada_numero: "",
        factura_relacionada_clave_acceso: "",
        nota_credito_motivo_tipo: motivoTipo || "DEVOLUCION",
        es_devolucion: (motivoTipo || "DEVOLUCION") === "DEVOLUCION",
        es_ajuste: (motivoTipo || "DEVOLUCION") === "AJUSTE",
      });
      return;
    }

    if (lastClienteFacturaRef.current === identificacion) return;
    lastClienteFacturaRef.current = identificacion;

    const cargarFacturasCliente = async () => {
      setLoadingFacturas(true);
      setFacturasError("");

      try {
        const res = await fetch(
          `/facturas/notas-credito/facturas-cliente/?identificacion=${encodeURIComponent(
            identificacion
          )}`,
          {
            credentials: "include",
          }
        );

        const json = await res.json().catch(() => ({}));

        if (!res.ok || !json.ok) {
          setFacturasCliente([]);
          setFacturasError(json.error || "No se pudieron cargar las facturas del cliente");
          return;
        }

        const facturas = Array.isArray(json.facturas) ? json.facturas : [];
        setFacturasCliente(facturas);

        if (!facturas.length) {
          onChange?.({
            factura_relacionada_id: "",
            factura_relacionada_numero: "",
            factura_relacionada_clave_acceso: "",
            nota_credito_motivo_tipo: motivoTipo || "DEVOLUCION",
            es_devolucion: (motivoTipo || "DEVOLUCION") === "DEVOLUCION",
            es_ajuste: (motivoTipo || "DEVOLUCION") === "AJUSTE",
          });
          return;
        }

        const actual = facturas.find(
          (f) => String(f.id) === String(facturaRelacionadaId || "")
        );

        if (actual) {
          onChange?.({
            factura_relacionada_id: String(actual.id),
            factura_relacionada_numero: actual.numero_documento || "",
            factura_relacionada_clave_acceso: actual.clave_acceso || "",
            nota_credito_motivo_tipo: motivoTipo || "DEVOLUCION",
            es_devolucion: (motivoTipo || "DEVOLUCION") === "DEVOLUCION",
            es_ajuste: (motivoTipo || "DEVOLUCION") === "AJUSTE",
          });
          return;
        }

        const primera = facturas[0];

        onChange?.({
          factura_relacionada_id: String(primera.id),
          factura_relacionada_numero: primera.numero_documento || "",
          factura_relacionada_clave_acceso: primera.clave_acceso || "",
          nota_credito_motivo_tipo: motivoTipo || "DEVOLUCION",
          es_devolucion: (motivoTipo || "DEVOLUCION") === "DEVOLUCION",
          es_ajuste: (motivoTipo || "DEVOLUCION") === "AJUSTE",
        });
      } catch {
        setFacturasCliente([]);
        setFacturasError("No se pudieron cargar las facturas del cliente");
      } finally {
        setLoadingFacturas(false);
      }
    };

    cargarFacturasCliente();
  }, [identificacion]);

  const handleFacturaRelacionadaChange = (e) => {
    const facturaId = String(e.target.value || "");
    const factura = facturasCliente.find((f) => String(f.id) === facturaId);

    onChange?.({
      factura_relacionada_id: facturaId,
      factura_relacionada_numero: factura?.numero_documento || "",
      factura_relacionada_clave_acceso: factura?.clave_acceso || "",
      nota_credito_motivo_tipo: motivoTipo || "DEVOLUCION",
      es_devolucion: (motivoTipo || "DEVOLUCION") === "DEVOLUCION",
      es_ajuste: (motivoTipo || "DEVOLUCION") === "AJUSTE",
    });
  };

  const handleMotivoTipoChange = (tipo) => {
    onChange?.({
      factura_relacionada_id: facturaRelacionadaId || "",
      factura_relacionada_numero: facturaRelacionadaNumero || "",
      factura_relacionada_clave_acceso: facturaRelacionadaClaveAcceso || "",
      nota_credito_motivo_tipo: tipo,
      es_devolucion: tipo === "DEVOLUCION",
      es_ajuste: tipo === "AJUSTE",
    });
  };

  const handleLoadProducts = async () => {
    if (!facturaRelacionadaId || loadingProducts) return;

    setLoadingProducts(true);

    try {
      const res = await fetch(
        `/facturas/notas-credito/detalle-factura/${facturaRelacionadaId}/`,
        {
          credentials: "include",
        }
      );

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json.ok) return;

      const productos = normalizeLoadedProducts(json.productos || []);
      onProductsLoaded?.(productos, json);
    } catch {
    } finally {
      setLoadingProducts(false);
    }
  };

  return (
    <TableRow>
      <TableCell sx={tdLabelSx}>Documento Afectado:</TableCell>
      <TableCell sx={tdFieldSx} colSpan={2}>
        {!clienteSeleccionado ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "#fafafa",
              borderColor: "#e5e7eb",
              maxWidth: 760,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#1f2937", mb: 0.5 }}>
              Selecciona primero el cliente
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Cuando elijas el cliente, aquí podrás seleccionar la factura a la que se aplicará la nota de crédito y definir si corresponde a devolución o ajuste.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2} sx={{ maxWidth: 760 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: "#dbe2ea",
                bgcolor: "#fcfcfd",
              }}
            >
              <Stack spacing={1.5}>
                <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
                  Factura del cliente
                </Typography>

                {loadingFacturas ? (
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Cargando facturas autorizadas...
                    </Typography>
                  </Stack>
                ) : facturasError ? (
                  <Typography variant="body2" sx={{ color: "#c62828" }}>
                    {facturasError}
                  </Typography>
                ) : facturasCliente.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    Este cliente no tiene facturas autorizadas disponibles para nota de crédito.
                  </Typography>
                ) : (
                  <>
                    <Select
                      value={facturaRelacionadaId}
                      onChange={handleFacturaRelacionadaChange}
                      displayEmpty
                      sx={{ minWidth: 420 }}
                    >
                      {facturasCliente.map((factura) => (
                        <MenuItem key={factura.id} value={String(factura.id)}>
                          {buildFacturaLabel(factura)}
                        </MenuItem>
                      ))}
                    </Select>

                    {facturaRelacionadaId && (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "#ffffff",
                          borderColor: "#eceff3",
                        }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ color: "#374151" }}>
                            <strong>Documento:</strong> {facturaRelacionadaNumero || "-"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#374151" }}>
                            <strong>Clave de acceso:</strong> {facturaRelacionadaClaveAcceso || "-"}
                          </Typography>
                        </Stack>
                      </Paper>
                    )}

                    <Button
                      variant="contained"
                      onClick={handleLoadProducts}
                      disabled={!facturaRelacionadaId || loadingProducts}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {loadingProducts ? "Cargando productos..." : "Cargar productos"}
                    </Button>
                  </>
                )}
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: "#dbe2ea",
                bgcolor: "#fcfcfd",
              }}
            >
              <Stack spacing={1.25}>
                <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
                  Motivo de la nota de crédito
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Paper
                    variant="outlined"
                    onClick={() => handleMotivoTipoChange("DEVOLUCION")}
                    sx={{
                      flex: 1,
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor: motivoTipo === "DEVOLUCION" ? "#1976d2" : "#e0e0e0",
                      bgcolor: motivoTipo === "DEVOLUCION" ? "#f4f8ff" : "#fff",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={motivoTipo === "DEVOLUCION"}
                          onChange={() => handleMotivoTipoChange("DEVOLUCION")}
                        />
                      }
                      label="Devolución"
                      sx={{ alignItems: "flex-start", m: 0 }}
                    />
                    <Typography variant="body2" sx={{ color: "#6b7280", pl: 4.5 }}>
                      Úsalo cuando se revierte total o parcialmente una venta por devolución de productos.
                    </Typography>
                  </Paper>

                  <Paper
                    variant="outlined"
                    onClick={() => handleMotivoTipoChange("AJUSTE")}
                    sx={{
                      flex: 1,
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor: motivoTipo === "AJUSTE" ? "#1976d2" : "#e0e0e0",
                      bgcolor: motivoTipo === "AJUSTE" ? "#f4f8ff" : "#fff",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={motivoTipo === "AJUSTE"}
                          onChange={() => handleMotivoTipoChange("AJUSTE")}
                        />
                      }
                      label="Ajuste"
                      sx={{ alignItems: "flex-start", m: 0 }}
                    />
                    <Typography variant="body2" sx={{ color: "#6b7280", pl: 4.5 }}>
                      Úsalo cuando necesitas corregir valores, descuentos, errores de emisión o diferencias en la factura.
                    </Typography>
                  </Paper>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}